import * as React from "react";

import { cn } from "@/lib/utils";
import {
  FORM_CONTROL_NUMBER_INPUT_CLASS,
  FORM_CONTROL_QUANTITY_PLACEHOLDER,
} from "@/components/ui/form-control";
import { Input } from "@/components/ui/input";

export interface QuantityInputProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    "value" | "onChange" | "type" | "min" | "max" | "step"
  > {
  /** Current quantity — a finite integer. */
  value: number;
  /** Fires with the clamped integer on every edit. */
  onChange: (value: number) => void;
  /** Lower bound (default 1). Pass 0 to allow an empty/"none" cell. */
  min?: number;
  /** Upper bound (default 999). This is also the integer-overflow guard: a
   *  value can never exceed it, so it can't blow past Postgres' int range. */
  max?: number;
}

/**
 * QuantityInput — the ONE quantity field for the whole app.
 *
 * Why a shared component: every product/line quantity must be an integer within
 * a sane range. A bare `<input type="number">` lets a user paste e.g. a phone
 * number and overflow Postgres' 32-bit `integer` column (error 22003). This
 * clamps to `[min, max]` (default `[1, 999]`) on every keystroke and on paste,
 * blocks exponent/sign characters, hides the cramped native spinners, and uses
 * `tabular-nums` so multi-digit values stay readable. Scroll wheel does not
 * change the value while focused (blur on wheel so the page scrolls instead).
 *
 * Controlled with a plain number (the shape most qty state already uses), so
 * it's a drop-in for the inline `<Input type="number">` qty fields it replaces.
 *
 * @example
 * <QuantityInput value={qty} onChange={setQty} className="w-20" aria-label="Quantity" />
 */
export const QuantityInput = React.forwardRef<HTMLInputElement, QuantityInputProps>(
  (
    {
      value,
      onChange,
      min = 1,
      max = 999,
      className,
      placeholder = FORM_CONTROL_QUANTITY_PLACEHOLDER,
      onWheel,
      "aria-label": ariaLabel,
      ...props
    },
    ref,
  ) => {
    const clamp = (n: number) => Math.min(max, Math.max(min, Math.trunc(n)));

    return (
      <Input
        ref={ref}
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        step={1}
        placeholder={placeholder}
        aria-label={ariaLabel ?? "Quantity"}
        value={Number.isFinite(value) ? value : min}
        onChange={(e) => {
          const parsed = parseInt(e.target.value, 10);
          onChange(Number.isFinite(parsed) ? clamp(parsed) : min);
        }}
        onKeyDown={(e) => {
          /**
           * Integer-only: block exponent / sign / decimal characters that
           * <input type="number"> otherwise accepts.
           */
          if (["e", "E", "+", "-", "."].includes(e.key)) e.preventDefault();
        }}
        onWheel={(e) => {
          /** Browsers increment/decrement number inputs on scroll while focused. */
          e.currentTarget.blur();
          onWheel?.(e);
        }}
        className={cn(FORM_CONTROL_NUMBER_INPUT_CLASS, className)}
        {...props}
      />
    );
  },
);
QuantityInput.displayName = "QuantityInput";

export default QuantityInput;
